import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { BRAND, DIM } from "../theme/index.js";

type BadgeColor = "cyan" | "yellow" | "coral" | "green" | "purple";

const BADGE_CONFIG: Record<BadgeColor, { bg: string; text: string }> = {
  cyan:   { bg: DIM.cyan,   text: BRAND.cyan   },
  yellow: { bg: DIM.yellow, text: "#B89A00"    },
  coral:  { bg: DIM.coral,  text: BRAND.coral  },
  green:  { bg: DIM.green,  text: BRAND.green  },
  purple: { bg: DIM.purple, text: BRAND.purple },
};

interface BadgeChipProps {
  label: string;
  color?: BadgeColor;
  spinning?: boolean;
}

export function BadgeChip({ label, color = "cyan", spinning = false }: BadgeChipProps) {
  const { bg, text } = BADGE_CONFIG[color];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "2px 8px",
        borderRadius: "4px",
        backgroundColor: bg,
        color: text,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "'DM Sans', sans-serif",
        letterSpacing: "0.2px",
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
    >
      {spinning && <CircularProgress size={9} thickness={5} sx={{ color: "inherit" }} />}
      {label}
    </Box>
  );
}

// Map doc/websource status strings → badge color
export function statusToBadgeColor(status: string): BadgeColor {
  switch (status) {
    case "complete":   return "green";
    case "processing":
    case "crawling":   return "cyan";
    case "pending":    return "yellow";
    case "failed":     return "coral";
    default:           return "cyan";
  }
}

// Map bot state strings → badge color
export function botStateToBadgeColor(state: string): BadgeColor {
  switch (state) {
    case "active":          return "green";
    case "paused":          return "yellow";
    case "quota_exhausted":
    case "deleted":
    case "hard_deleted":    return "coral";
    case "deactivated":     return "purple";
    default:                return "cyan";
  }
}

export function botStateLabel(state: string): string {
  const LABELS: Record<string, string> = {
    active: "Active", paused: "Paused", deactivated: "Deactivated",
    quota_exhausted: "Quota Exceeded", deleted: "Deleted", hard_deleted: "Deleted",
  };
  return LABELS[state] ?? state;
}
